// ✅ Service ATUALIZADO para integração REAL com ChatGPT
// SUBSTITUA o arquivo lib/assistenteService.ts existente por este código

import { supabase } from '@features/sistema/services/supabaseClient';

import { contextService } from './contextService.js';
import { openaiService } from './openaiService';

// ---- Resumo de contratos por empresa (RPC) ----
export type ResumoContratosEmpresa = {
  empresa_id: string | null;
  empresa_nome: string | null;
  n_contratos: number;
  valor_contrato_inicial: number;
  valor_aditivos_aprovados: number;
  valor_contrato_total: number;
  total_medido: number;
  total_pago: number;
  saldo: number;
  pct_cronograma: number; // %
  pct_pago: number;       // %
};

export async function getResumoContratosPorEmpresa(obraId: string, apenasAtivos = true) {
  const { data, error } = await supabase.rpc("fn_resumo_contratos_por_empresa", {
    p_obra: obraId,
    p_apenas_ativos: apenasAtivos,
  });
  if (error) throw error;
  return (data ?? []) as ResumoContratosEmpresa[];
}

// Cria um client Scoped para o schema ACOR (Supabase JS v2+)
const dbAcor = typeof (supabase as any).schema === 'function'
  ? (supabase as any).schema('acor')
  : null;

const LAST_OBRA_KEY = 'last_obra_id';

function isUUID(v?: string | null): boolean {
  return !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

function parseObraIdFromLocation(): string | undefined {
  try {
    const { pathname, search, hash } = window.location ?? { pathname: '', search: '', hash: '' };
    const all = `${pathname}${search}${hash}`;
    const qs = new URLSearchParams(search);
    const qp = qs.get('obra') || qs.get('obra_id') || qs.get('obraId');
    if (isUUID(qp)) return qp!;
    // Suporte a diversos padrões de rota (com ou sem tenant)
    const patterns = [
      /(?:^|\/)(?:obras|obra)\/([0-9a-f-]{36})(?:\/|$)/i,
      /obraId=([0-9a-f-]{36})/i,
      /(?:^|\/)(?:diarios|tarefas|servicos)\/([0-9a-f-]{36})(?:\/|$)/i
    ];
    for (const re of patterns) {
      const m = re.exec(all);
      if (m && isUUID(m[1])) return m[1];
    }
  } catch { /* no-op */ }
  return undefined;
}

async function lookupObraById(id: string): Promise<{ id: string, nome?: string } | null> {
  if (!isUUID(id)) return null;
  const { data, error } = await supabase
    .from('obras')
    .select('id,nome,is_personal')
    .eq('id', id)
    .maybeSingle();
  if (error) return { id };
  // Rejeita obras pessoais
  if (data?.is_personal === true) return null;
  return data || null;
}

function extractNameCandidatesFromText(text?: string): string[] {
  if (!text) return [];
  const t = text.trim();
  const cand: string[] = [];
  // Dentro de aspas
  const quoted = Array.from(t.matchAll(/"([^"]{3,80})"|"([^"]{3,80})"|\'([^']{3,80})\'/g))
    .map(m => m[1] || m[2] || m[3]).filter(Boolean);
  cand.push(...quoted);
  // Após palavra "obra" ou "projeto"
  const afterObra = Array.from(t.matchAll(/\b(?:obra|projeto)\s+([A-Za-z0-9][\w\s\-\/]{2,80})/gi))
    .map(m => m[1]).filter(Boolean);
  cand.push(...afterObra);
  // Normaliza e dedup
  return Array.from(new Set(cand.map(s => s.trim()))).slice(0, 3);
}

async function lookupObraByNameFragment(q: string): Promise<{ id: string, nome: string } | null> {
  // Simples e eficiente com índice btree on lower(nome) ou trigram se disponível
  const { data, error } = await supabase
    .from('obras')
    .select('id,nome,is_personal')
    .or('is_personal.is.null,is_personal.eq.false')
    .ilike('nome', `%${q}%`)
    .limit(50);
  if (error || !data || data.length === 0) return null;
  const obrasValidas = (data||[]).filter((o:any)=> o.is_personal !== true);
  if (obrasValidas.length === 0) return null;
  // Heurística: pega a mais curta (geralmente nome oficial) ou a primeira
  const best = obrasValidas.sort((a, b) => (a.nome?.length || 999) - (b.nome?.length || 999))[0];
  return best || null;
}

type ResolveResult = { obraId?: string, obraNome?: string, source: string, confidence: number };
export async function resolveObraId(opts: { explicitObraId?: string | null, userMessage?: string | null }): Promise<ResolveResult> {
  // 1) Explícito (chamada já manda obraId)
  if (isUUID(opts.explicitObraId || '')) {
    const hit = await lookupObraById(opts.explicitObraId!);
    if (hit) {
      localStorage.setItem(LAST_OBRA_KEY, hit.id);
      return { obraId: hit.id, obraNome: hit.nome, source: 'explicit', confidence: 1.0 };
    }
  }
  // 2) URL/QueryString (Sidebar observa rota atual)
  const byUrl = parseObraIdFromLocation();
  if (isUUID(byUrl)) {
    const hit = await lookupObraById(byUrl!);
    if (hit) {
      localStorage.setItem(LAST_OBRA_KEY, hit.id);
      return { obraId: hit.id, obraNome: hit.nome, source: 'url', confidence: 0.95 };
    }
  }
  // 3) Última obra aberta (persistida)
  const last = localStorage.getItem(LAST_OBRA_KEY);
  if (isUUID(last)) {
    const hit = await lookupObraById(last!);
    if (hit) {
      return { obraId: hit.id, obraNome: hit.nome, source: 'last_used', confidence: 0.8 };
    }
  }
  // 4) Heurística por nome citado na pergunta
  const cands = extractNameCandidatesFromText(opts.userMessage || '');
  for (const c of cands) {
    const hit = await lookupObraByNameFragment(c);
    if (hit) {
      localStorage.setItem(LAST_OBRA_KEY, hit.id);
      return { obraId: hit.id, obraNome: hit.nome, source: 'nlp_name', confidence: 0.7 };
    }
  }
  // Falhou: sem obra detectada
  return { source: 'unknown', confidence: 0 };
}

// Alias semântico para o chat/Sidebar
export async function detectObraForChat(userMessage?: string | null, explicitObraId?: string | null) {
  return resolveObraId({ explicitObraId, userMessage });
}

// ====== Detecção de intenção "alterações" ======
const ALTERACOES_REGEX =
  /\b(altera(ç|c)[aã]o(?:es)?|mudan[çc]a(?:s)?|revis(ã|a)o(?:es)?|aditivo(?:s)?|rfi|change\s*order|as[-\s]*built|asbuilt)\b/i;
function isPerguntaAlteracoes(txt?: string) {
  return !!txt && ALTERACOES_REGEX.test(txt);
}

// ====== Utilitários locais (sem alterar relatoriosService) ======
function toISODate(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
}
function normalizeAlteracoes(json: any): any[] {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (typeof json === 'object') return [json];
  return [];
}
function linhaDoItem(item: any, fallbackData: string): string {
  if (item == null) return '(vazio)';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  const partes: string[] = [];
  const d = item?.data ? toISODate(item.data) : fallbackData;
  if (d) partes.push(d);
  if (item?.tipo) partes.push(`[${item.tipo}]`);
  const titulo = (item?.titulo || '').trim();
  const desc = (item?.descricao || '').trim();
  const corpo = [titulo, desc].filter(Boolean).join(' — ');
  if (corpo) partes.push(corpo);
  return partes.length ? partes.join(' ') : JSON.stringify(item);
}

async function fetchAlteracoesProjetoDaObra(obraId: string) {
  const { data, error } = await supabase
    .from('relatorios')
    .select('id, numero, data, tipo, alteracoes_projeto')
    .eq('obra_id', obraId)
    .eq('tipo', 'relatorio_diario')
    .not('alteracoes_projeto', 'is', null)
    .order('data', { ascending: true }); // cronológico
  if (error || !data) return [];
  const out: { relatorio_id: string; numero: number | null; linhas: string[] }[] = [];
  for (const r of data as any[]) {
    const arr = normalizeAlteracoes(r.alteracoes_projeto);
    const linhas = arr.map((it: any) => linhaDoItem(it, toISODate(r.data)));
    out.push({ relatorio_id: r.id, numero: r.numero ?? null, linhas });
  }
  return out;
}

// ===== Intenções (pt-BR) → RPCs
const PT = {
  termometro: /\b(term[oó]metro|vis[aã]o geral|resumo da obra)\b/i,
  prazos: /\b(prazo|vence|vencem|esta semana|7 dias|agenda|calend[aá]rio)\b/i,
  subetapasAtraso: /\b(subetapa).*(atras|atraso)\b/i,
  servicosRisco: /\b(servi[cç]o|empreitada).*(risco|atras|n[aã]o iniciad|n[aã]o terminou)\b/i,
  comprasRisco: /\b(requisi[cç][aã]o|compra|or[cç]amento|pedido).*(prazo|urg|atras)\b/i,
  threads: /\b(thread|conversa|coment[aá]rio|discuss[aã]o).*(quente|recent[ea])\b/i,
  pendencias: /\b(pend[eê]ncia|backlog|prioridade|cr[ií]tica|alta)\b/i,
  ocorrencias: /\b(ocorr[eê]ncia|incidente|intercorr[eê]ncia)\b/i,
  clima: /\b(clima|chuva|produtividade)\b/i,
  efetivo: /\b(efetivo|m[aã]o[-\s]*de[-\s]*obra|equipamento|equipamentos)\b/i,
  checklists: /\b(checklist|lista de verifica[cç][aã]o)\b/i,
  parados: /\b(parado|parados|travado|travados)\b/i,
};

const fmtDate = (d?: string | null) => (d ? String(d).slice(0, 10) : '');
const headObra = (nome?: string, id?: string) =>
  `📍 **Obra**: ${nome?.trim() || (id ? `#${id.slice(0,8)}…` : 'N/D')}\n`;

// Helper RPC único
async function rpc<T=any>(fn: string, args: Record<string, any>): Promise<T[]> {
  // 1) se houver suporte a schema(), usa Content-Profile: acor
  if (dbAcor) {
    const { data, error, status } = await dbAcor.rpc(fn, args);
    if (!error) return (data || []) as T[];
    // Se 404/PGRST202, tenta fallback público (wrapper)
    if (status === 404 || (error as any)?.code === 'PGRST202') {
      console.warn(`ℹ️ RPC ${fn} no schema acor não encontrada; tentando public wrapper...`, error);
    } else {
      throw error;
    }
  }
  // 2) fallback: chama em public (precisa dos wrappers criados no Passo 2)
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    console.error(`❌ Erro RPC ${fn}:`, error);
    throw new Error(`RPC ${fn} não encontrada. Configure o search_path, exponha o schema 'acor' no API, ou crie wrappers no schema public.`);
  }
  return (data || []) as T[];
}

async function handleTermometro(obraId: string, obraNome?: string) {
  const [t] = await rpc('fn_termometro_obra', { p_obra: obraId });
  if (!t) return headObra(obraNome, obraId) + 'Sem dados para o termômetro.';
  return [
    headObra(obraNome, obraId) + '**Termômetro da Obra**',
    `- Tarefas: ${t.tarefas_abertas} abertas • ${t.tarefas_atrasadas} atrasadas • ${t.tarefas_concluidas} concluídas`,
    `- Subetapas: ${t.subetapas_andamento} em andamento • ${t.subetapas_paradas} paradas • ${t.subetapas_concluidas} concluídas`,
    `- Serviços: ${t.servicos_andamento} em andamento • ${t.servicos_parados} parados • ${t.servicos_concluidos} concluídos`,
  ].join('\n');
}

async function handleSubetapasAtraso(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_subetapas_atraso', { p_obra: obraId });
  const items = rows.filter((r:any)=>r.atraso_dias>0)
    .map((r:any)=>`- ${r.nome} — atraso ${r.atraso_dias}d (prev. ${fmtDate(r.data_prevista_fim)}) — id:${r.subetapa_id}`);
  return headObra(obraNome, obraId)+`**Subetapas com atraso**\n`+(items.join('\n')||'Sem atrasos.');
}

async function handleComprasRisco(obraId: string, obraNome?: string, janela=7) {
  const rows = await rpc('fn_requisicoes_em_risco', { p_obra: obraId, p_janela: janela });
  const items = rows.map((r:any)=>`- ${r.urgencia} • entrega ${fmtDate(r.prazo_entrega)} (D${r.dias_para_prazo>=0?'-':''}${r.dias_para_prazo}) — ${r.texto||''} — id:${r.id}`);
  return headObra(obraNome, obraId)+`**Requisições em risco (≤ ${janela} dias / sem prazo)**\n`+(items.join('\n')||'Sem riscos.');
}

// ===== Config de debug (opcional) =====
const DEBUG_SHOW_IDS = false; // deixe true só quando quiser depurar

// ============== HELPERS DE FORMATAÇÃO ==============
const dateIso = (d: any) => {
  try { return new Date(d).toISOString().slice(0,10) } catch { return String(d).slice(0,10) }
}
const tinyId = (id?: string) => (id ? id.slice(0,8) : '');
const escapeMd = (s?: string) => (s || '').replace(/([_*`~|>])/g, '\\$1');

// rotas internas para montar links
const linkTo = (obraId: string, tipo?: string, id?: string) => {
  if (!obraId || !id) return null;
  switch ((tipo || '').toLowerCase()) {
    case 'tarefa':
      return `/obras/${obraId}/tarefas/${id}`;
    case 'servico':
      return `/obras/${obraId}/servicos/${id}`;
    case 'subetapa':
    case 'subetapas_obra':
      return `/obras/${obraId}/cronograma?sub=${id}`;
    case 'requisicao':
    case 'compras':
    case 'compras_requisicao':
      return `/obras/${obraId}/compras/requisicoes/${id}`;
    case 'relatorio':
    case 'diario':
      return `/obras/${obraId}/diarios?focus=${id}`;
    default:
      return null;
  }
};

const mdLink = (text: string, href: string | null) =>
  href ? `[${escapeMd(text)}](${href})` : escapeMd(text);

function formatParados(obraNome: string, obraId: string, rows: Array<{tipo:string,id?:string,titulo?:string,desde?:string|Date}>) {
  if (!rows?.length) return `Não encontrei itens parados na obra **${obraNome}**.`;
  const head = `**Itens parados — ${obraNome}** (Top ${Math.min(rows.length, 10)})`;
  const body = rows.slice(0,10).map(r => {
    const dt = r.desde ? dateIso(r.desde) : '';
    const href = linkTo(obraId, r.tipo, r.id);
    const name = r.titulo || r.id || '(sem título)';
    const label = r.tipo === 'servico' ? 'Serviço' : r.tipo === 'tarefa' ? 'Tarefa' : 'Item';
    const tail = href ? ` [abrir](${href})` : (r.id ? ` (${tinyId(r.id)})` : '');
    return `• ${dt} • ${label} — **${name}**${tail}`;
  }).join('\n');
  return `${head}\n${body}`;
}

function formatChecklists(obraNome: string, resumo: { reprovados?: number, disciplinas?: string[], itens?: any[] }) {
  const rep = resumo.reprovados ?? resumo.itens?.length ?? 0;
  const head = `**Checklists recentes — ${obraNome}**`;
  const disc = (resumo.disciplinas || []).slice(0,3).map(d=>`"${d}"`).join(', ');
  const linha1 = `• Itens reprovados: **${rep}**`;
  const linha2 = disc ? `• Disciplinas com mais movimentação: ${disc}` : '';
  const detalhes = (resumo.itens || []).slice(0,5).map((it:any)=>{
    const dt = it.data ? dateIso(it.data) : '';
    const nm = it.titulo || it.nome || it.id || 'Item';
    return `  - ${dt} • ${nm}`;
  }).join('\n');
  return [head, linha1, linha2, detalhes].filter(Boolean).join('\n');
}

function formatThreads(obraNome: string, obraId: string, rows: Array<{thread_id?:string,recurso?:string,recurso_id?:string,last_at?:string}>) {
  if (!rows?.length) return `Sem threads quentes na obra **${obraNome}**.`;
  const head = `**Threads quentes — ${obraNome}** (Top ${Math.min(rows.length, 6)})`;
  const body = rows.slice(0,6).map(r=>{
    const dt = r.last_at ? new Date(r.last_at).toLocaleString() : '';
    const tipo = (r.recurso || '').toLowerCase();
    const href = linkTo(obraId, tipo, r.recurso_id);
    const tail = href ? ` [abrir](${href})` : (r.recurso_id ? ` (${tinyId(r.recurso_id)})` : '');
    const label = r.recurso ? r.recurso : 'Thread';
    return `• ${dt} • ${label}${tail}`;
  }).join('\n');
  return `${head}\n${body}`;
}

function formatPrazos(obraNome: string, obraId: string, rows: Array<any>, janelaDias = 7) {
  const head = `📍 **Obra**: ${escapeMd(obraNome)}\n**Prazos (≤ ${janelaDias} dias)**\n`;
  if (!rows?.length) return `${head}\nNenhum prazo na janela.`;
  const body = rows.map((r:any) => {
    const dt   = r.data_ref ? dateIso(r.data_ref) : '';
    const tipo = (r.tipo || '').toLowerCase(); // 'tarefa' | 'subetapa' | ...
    const nome = r.titulo || r.nome || r.descricao || r.texto || '(sem título)';
    const href = linkTo(obraId, tipo, r.id);
    const line = `${dt} • ${tipo} • ${mdLink(nome, href)} ${r.status ? `(${r.status})` : ''}`;
    return DEBUG_SHOW_IDS && r.id ? `${line} — id:${r.id}` : line;
  }).join('\n');
  return `${head}${body}`;
}

function formatServicosRisco(obraNome: string, obraId: string, rows: Array<any>) {
  const head = `📍 **Obra**: ${escapeMd(obraNome)}\n**Serviços em risco**\n`;
  if (!rows?.length) return `${head}\nNenhum serviço em risco no momento.`;
  const body = rows.map((r:any) => {
    const risco = r.risco || r.motivo || '';
    const nome  = r.nome  || r.titulo || '(sem nome)';
    const ref   = r.data_ref ? dateIso(r.data_ref) : '';
    const emp   = r.empresa || r.nome_empresa || 's/empresa';
    const href  = linkTo(obraId, 'servico', r.id);
    const line  = `${escapeMd(risco)} • ${mdLink(nome, href)} — ${escapeMd(emp)} — ref: ${ref}`;
    return DEBUG_SHOW_IDS && r.id ? `${line} — id:${r.id}` : line;
  }).join('\n');
  return `${head}${body}`;
}

async function handleThreads(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_threads_quentes', { p_obra: obraId, p_limite: 10 });
  return formatThreads(obraNome || 'Obra', obraId, rows);
}

async function handlePrazosSemana(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_prazos_obra', { p_obra: obraId, p_dias: 7 });
  return formatPrazos(obraNome || 'Obra', obraId, rows, 7);
}

async function handleServicosEmRisco(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_servicos_em_risco', { p_obra: obraId });
  return formatServicosRisco(obraNome || 'Obra', obraId, rows);
}

async function handlePendencias(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_top_pendencias', { p_obra: obraId, p_limite: 5 });
  const items = rows.map((r:any)=>`- ${fmtDate(r.data_limite)} • **${r.nome}** [${r.prioridade}] (${r.status}) — id:${r.id}`);
  return headObra(obraNome, obraId)+`**Top pendências**\n`+(items.join('\n')||'Sem pendências.');
}

async function handleOcorrencias(obraId: string, obraNome?: string, dias=14) {
  const rows = await rpc('fn_ocorrencias_diario', { p_obra: obraId, p_dias: dias });
  const items = rows.map((r:any)=>`- ${fmtDate(r.data)} • Diário #${r.numero} — ${r.ocorrencias?.slice(0,180)}`);
  return headObra(obraNome, obraId)+`**Ocorrências (últimos ${dias} dias)**\n`+(items.join('\n')||'Sem ocorrências.');
}

async function handleClima(obraId: string, obraNome?: string, dias=30) {
  const [r] = await rpc('fn_clima_resumo', { p_obra: obraId, p_dias: dias });
  if (!r) return headObra(obraNome, obraId)+'Sem dados de clima.';
  return [
    headObra(obraNome, obraId)+`**Clima x Produtividade (últimos ${dias} dias)**`,
    `- Dias de chuva: ${r.dias_chuva}`,
    `- Dias secos: ${r.dias_seco}`,
    `- Tarefas concluídas: ${r.tarefas_concluidas}`,
  ].join('\n');
}

async function handleEfetivo(obraId: string, obraNome?: string, dias=14) {
  const rows = await rpc('fn_efetivo_e_equipamentos', { p_obra: obraId, p_dias: dias });
  const agg = new Map<string, number>();
  (rows as any[]).forEach(r => {
    const k = `${r.tipo}:${r.nome}`; agg.set(k, (agg.get(k)||0) + Number(r.quantidade||0));
  });
  const top = [...agg.entries()].sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k,v])=>`- ${k.replace(':',' • ')} — total ${v}`);
  return headObra(obraNome, obraId)+`**Efetivo & Equipamentos (últimos ${dias} dias)**\n`+(top.join('\n')||'Sem registro.');
}

async function handleChecklists(obraId: string, obraNome?: string, dias=30) {
  const rows = await rpc('fn_checklists_recentes', { p_obra: obraId, p_dias: dias });
  const reprovados = rows?.filter((r:any) => (r.comentarios||'').toLowerCase().includes('reprov')).length || 0;
  // opcional: extrair "disciplinas" pelo texto
  const disciplinas = Array.from(
    new Set(
      (rows||[])
        .map((r:any)=> (r.comentarios||'').match(/(Serviços Iniciais|Pisos e Revestimentos|Forro|Elétrica|Hidráulica)/gi))
        .flat()
        .filter(Boolean)
    )
  );
  return formatChecklists(obraNome || 'Obra', { reprovados, disciplinas, itens: rows });
}

async function handleParados(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_parados', { p_obra: obraId });
  return formatParados(obraNome || 'Obra', obraId, rows);
}

// ===== Formatter: progresso por empresa =====
function formatProgressoEmp(obraNome: string, obraId: string, rows: any[]) {
  const head = `📍 **Obra**: ${obraNome}\n**Progresso por empresa (cronograma)**\n`;
  if (!rows?.length) return `${head}\nSem dados para esta obra.`;
  const body = rows
    .map((r:any) => {
      const pct = Math.max(0, Math.min(100, Number(r.pct_concluido ?? 0)));
      const nome = r.empresa_nome || 'Sem empresa';
      const href = r.empresa_id ? `/obras/${obraId}/cronograma?empresa=${r.empresa_id}` : `/obras/${obraId}/cronograma`;
      return `- [${nome}](${href}) — **${pct.toFixed(1)}%** (${Math.round(Number(r.dias_concluidos||0))}/${Math.round(Number(r.dias_previstos||0))} dias, ${r.qtd_subetapas||0} subetapas)`;
    })
    .join('\n');
  return `${head}${body}`;
}

async function handleProgressoPorEmpresa(obraId: string, obraNome?: string) {
  const rows = await rpc('fn_progresso_por_empresa', { p_obra: obraId, p_parcial: true, p_incluir_sem_empresa: true });
  return formatProgressoEmp(obraNome || 'Obra', obraId, rows);
}

// util local para YYYY-MM-DD sem UTC
const ymdLocal = (d: Date) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

/* =========================
   Intenções de DIÁRIOS (como no seu arquivo)
   ========================= */

type DiarioFocus =
  | 'resumo' | 'comentarios' | 'alteracoes' | 'atividades'
  | 'ocorrencias' | 'clima' | 'mao_obra' | 'equipamentos';

function detectDiariosIntent(msg: string): {scope: 'semana'|'padrao', focus: DiarioFocus} | null {
  const m = msg.toLowerCase();

  const scope: 'semana'|'padrao' = /semana/.test(m) ? 'semana' : 'padrao';
  const focus: DiarioFocus =
    /coment[áa]rios?/.test(m) ? 'comentarios' :
    /altera[cç][õo]es?|projeto/.test(m) ? 'alteracoes' :
    /ocorr[êe]ncias?/.test(m) ? 'ocorrencias' :
    /atividades?/.test(m) ? 'atividades' :
    /\bclima\b/.test(m) ? 'clima' :
    /m[ãa]o de obra|mao_?obra/.test(m) ? 'mao_obra' :
    /equipamentos?/.test(m) ? 'equipamentos' :
    'resumo';

  if (/(di[áa]rios?|relat[óo]rios?)/.test(m)) return { scope, focus };
  return null;
}

/* =========================
   (NOVO) Inteligência “Serviços ⇒ Materiais”
   ========================= */

type MateriaisScope = 'esta'|'proxima';
type CategoriaKey = 'demolicao'|'eletrica'|'hidro'|'estrutura'|'pintura'|'drywall'|'climatizacao'|'revest'|'imperme';

type ServicoRow = {
  id: string;
  nome: string;
  descricao?: string|null;
  status: 'nao_iniciado'|'agendado'|'em_andamento'|'parado'|'concluido';
  data_prevista_inicio?: string|null;
  data_prevista_fim?: string|null;
  data_inicio_real?: string|null;
  data_fim_real?: string|null;
  ativo?: boolean|null;
};

type ItemSug = { item: string; motivo?: string };
type PorServico = { servico_id: string; servico_nome: string; itens: ItemSug[] };
type PayloadCanonico = { por_servico: PorServico[]; observacoes_gerais?: string };

const norm = (s:string) => (s||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().trim();

function detectSugestoesSemana(msg: string): { scope: MateriaisScope } | null {
  const m = norm(msg);
  const pede = /(materiais|itens|sugestoes?)/.test(m);
  const sem = /(semana|esta\s+semana|essa\s+semana|prox(ima)?\s+semana)/.test(m);
  if (!pede || !sem) return null;
  return { scope: /prox/.test(m) ? 'proxima' : 'esta' };
}

function detectSugestoesPorCategoria(msg: string): { scope: MateriaisScope, cat: CategoriaKey } | null {
  const m = norm(msg);
  const pede = /(materiais|itens|sugestoes?)/.test(m);
  if (!pede) return null;
  const scope: MateriaisScope = /prox(ima)?\s+semana/.test(m) ? 'proxima' : 'esta';
  const map: Array<[CategoriaKey,RegExp]> = [
    ['eletrica', /(eletric|el[eé]trica|infra\s*el[eé]trica|ilumina[cç][aã]o|pontos?\s*el[eé]tricos?)/],
    ['hidro', /(hidro|hidrossanit[aá]rio|hidraulica|lavabo|pia|dreno)/],
    ['estrutura', /(estrutura|pilar|refor[cç]o|forma|viga|cinta)/],
    ['pintura', /(pintura|tinta|parede)/],
    ['drywall', /(drywall|gesso)/],
    ['climatizacao', /(climatiza[cç][aã]o|ar\s*cond|split)/],
    ['revest', /(revest|porcelanat|azulej|piso)/],
    ['imperme', /(imperme|manta)/],
    ['demolicao', /(demoli)/],
  ];
  for (const [cat, rx] of map) if (rx.test(m)) return { scope, cat };
  return null;
}

// Prefixo antes dos ":" define categoria com prioridade (ex.: "Elétrica: ...")
function leadingTagKey(nome: string): CategoriaKey | null {
  const tag = norm((nome||'').split(':')[0]);
  if (!tag) return null;
  if (/^eletric|^eletrica|^eletrica\/?telecom|^energia|^iluminacao$/.test(tag)) return 'eletrica';
  if (/^hidro|^hidrossanitario|^hidraulica$/.test(tag)) return 'hidro';
  if (/^estrutura$/.test(tag)) return 'estrutura';
  if (/^pintura$/.test(tag)) return 'pintura';
  if (/^drywall|^gesso$/.test(tag)) return 'drywall';
  if (/^climatizacao|^ar cond|^split$/.test(tag)) return 'climatizacao';
  if (/^revest|^acabamento$/.test(tag)) return 'revest';
  if (/^imperme/.test(tag)) return 'imperme';
  if (/^demolicao$/.test(tag)) return 'demolicao';
  return null;
}

const CATEGORIA_PATTERNS: Record<CategoriaKey, RegExp[]> = {
  demolicao: [/^demoli/i, /demoli/],
  // elétrica: padrão forte; bloqueio de climatização acontece na função matchCategoria
  eletrica: [/el[eé]tric|pontos?\s*el[eé]tric|infra(?:estrutura)?\s*el[eé]tric|eletrodut|ilumin[aã]c|luminaria/i],
  hidro: [/^hidro|hidross/i, /lavabo|pia|dreno/i],
  estrutura: [/^estrutura/i, /pilar|forma|reforc|viga|cinta/i],
  pintura: [/^pintur/i, /tinta|parede/i],
  drywall: [/drywall|gesso/i],
  climatizacao: [/climatiz|ar cond|split/i],
  revest: [/revest|porcelanat|azulej|piso/i],
  imperme: [/imperme|manta/i],
};

function matchCategoria(nome: string, cat: CategoriaKey) {
  const n = norm(nome);
  const lead = leadingTagKey(nome);
  if (lead) return lead === cat; // prefixo manda
  if (cat === 'eletrica' && /climatiz|ar cond|split/.test(n)) return false; // bloquear climatização em consultas elétricas
  return CATEGORIA_PATTERNS[cat].some(rx => rx.test(n));
}

function startOfWeek(d = new Date()) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // segunda = 0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0,0,0,0);
  return dt;
}
function getWeekRange(scope: MateriaisScope) {
  const ini = startOfWeek(scope === 'proxima' ? new Date(Date.now() + 7*86400000) : new Date());
  const fim = new Date(ini); fim.setDate(ini.getDate() + 6);
  return { ini: ymdLocal(ini), fim: ymdLocal(fim) };
}

async function fetchServicosSemana(obraId: string, scope: MateriaisScope) {
  const { ini, fim } = getWeekRange(scope);
  // serviços ativos, agendados ou em andamento, que intersectam a janela
  const { data, error } = await supabase
    .from('servicos')
    .select('id, nome, descricao, status, data_prevista_inicio, data_prevista_fim, data_inicio_real, data_fim_real, ativo')
    .eq('obra_id', obraId)
    .eq('ativo', true)
    .in('status', ['agendado','em_andamento'])
    .or(`and(data_prevista_inicio.lte.${fim},data_prevista_fim.gte.${ini}),and(data_inicio_real.lte.${fim},data_fim_real.gte.${ini})`)
    .order('data_prevista_inicio', { ascending: true });
  if (error) throw error;
  const rows = (data || []) as ServicoRow[];
  return { ini, fim, rows };
}

// ---------- Normalização do output ----------
function normalizeModelOutput(parsed: any, rows: ServicoRow[]): PayloadCanonico | null {
  if (!parsed) return null;
  const id2nome = new Map(rows.map(r => [r.id, r.nome]));
  if (parsed && Array.isArray(parsed.por_servico)) {
    const por_servico: PorServico[] = parsed.por_servico.map((s:any) => {
      const id = s.servico_id || s.id;
      const nome = s.servico_nome || id2nome.get(id) || '(sem nome)';
      const itens: ItemSug[] = (s.itens||[])
        .map((i:any)=> typeof i==='string'?({item:i}):({item:i?.item, motivo:i?.motivo}))
        .filter((i:any)=>i?.item);
      return { servico_id: id, servico_nome: nome, itens };
    });
    return { por_servico, observacoes_gerais: parsed.observacoes_gerais };
  }
  if (Array.isArray(parsed)) {
    const por_servico: PorServico[] = parsed.map((s:any)=> {
      const id = s.servico_id || s.id;
      const nome = s.servico_nome || id2nome.get(id) || '(sem nome)';
      const itens: ItemSug[] = (s.itens||[])
        .map((i:any)=> typeof i==='string'?({item:i}):({item:i?.item, motivo:i?.motivo}))
        .filter((i:any)=>i?.item);
      return { servico_id: id, servico_nome: nome, itens };
    });
    return { por_servico };
  }
  return null;
}

// ---------- Consolidação por categoria (lista única) ----------
function shortServiceName(n: string) { return (n||'').replace(/^[^:]+:\s*/,'').trim(); }
function normKeyItem(s: string) {
  const synonyms: Record<string,string> = {
    'conduite': 'conduíte', 'conduite 20/25': 'conduíte 20/25',
    'gesso/argamassa': 'argamassa/gesso', 'caixas de passagem': 'caixa de passagem',
    'caixinhas 4x2/4x4': 'caixinha 4x2/4x4', 'curvas/luvas': 'curva/luva',
    'tampas cegas': 'tampa cega', 'conectores': 'conector',
    'buchas e parafusos': 'bucha e parafuso',
  };
  if (!s) return '';
  let t = norm(s).replace(/\s+/g,' ');
  if (synonyms[t]) t = synonyms[t];
  t = t.replace(/\b(\w{4,})s\b/g, '$1'); // singular simples
  return t;
}
function pickDisplayLabel(variants: Set<string>) { return [...variants].sort((a,b)=>b.length-a.length)[0]; }
function consolidarListaUnicaDeEntradas(entradas: PorServico[], rowsRef: ServicoRow[]) {
  const mapa: Map<string, {labels:Set<string>, servicos:Set<string>}> = new Map();
  const id2nome = new Map(rowsRef.map(r=>[r.id,r.nome]));
  for (const s of (entradas||[])) {
    for (const it of (s.itens||[])) {
      const k = normKeyItem(it.item); if (!k) continue;
      const entry = mapa.get(k) || { labels: new Set<string>(), servicos: new Set<string>() };
      entry.labels.add((it.item||'').toLowerCase());
      entry.servicos.add(shortServiceName(s.servico_nome || id2nome.get(s.servico_id) || ''));
      mapa.set(k, entry);
    }
  }
  return [...mapa.entries()].map(([key,v])=>({
    key,
    label: pickDisplayLabel(v.labels),
    qtdeServicos: v.servicos.size,
    exemplos: [...v.servicos].slice(0,3)
  })).sort((a,b)=> b.qtdeServicos - a.qtdeServicos || a.label.localeCompare(b.label));
}
function labelCategoria(cat: CategoriaKey|'outras') {
  return ({
    demolicao:'Demolição', eletrica:'Elétrica', hidro:'Hidrossanitário', estrutura:'Estrutura',
    pintura:'Pintura', drywall:'Drywall', climatizacao:'Climatização', revest:'Revestimentos',
    imperme:'Impermeabilização', outras:'Outras'
  } as Record<string,string>)[cat] || 'Outras';
}
function inferCategoriaFromNome(nome: string): CategoriaKey|'outras' {
  const order: CategoriaKey[] = ['eletrica','hidro','estrutura','pintura','drywall','climatizacao','revest','imperme','demolicao'];
  for (const c of order) if (matchCategoria(nome, c)) return c;
  return 'outras';
}
function consolidarPorCategoria(canon: PayloadCanonico, rowsRef: ServicoRow[]) {
  const grupos = new Map<string, PorServico[]>();
  for (const s of (canon?.por_servico || [])) {
    const cat = inferCategoriaFromNome(s.servico_nome);
    if (!grupos.has(cat)) grupos.set(cat, []);
    grupos.get(cat)!.push(s);
  }
  const ordem: (CategoriaKey|'outras')[] = ['eletrica','hidro','estrutura','pintura','drywall','climatizacao','revest','imperme','demolicao','outras'];
  const result: Array<{ categoria: string, itens: Array<{ label: string, qtdeServicos: number, exemplos: string[] }> }> = [];
  for (const cat of ordem) {
    const entradas = grupos.get(cat) || []; if (!entradas.length) continue;
    const lista = consolidarListaUnicaDeEntradas(entradas, rowsRef); if (!lista.length) continue;
    result.push({ categoria: labelCategoria(cat), itens: lista });
  }
  return result;
}

// ---------- Geração com OpenAI (schema + crítica leve) ----------
const isGeneric = (s?: string) => !s ? true : /epi|protec(a|ã)o\s+de\s+area|diversos|materiais\s+gerais/i.test(norm(s));
const tooGenericService = (items: Array<{item:string}>) =>
  !items || items.length < 5 || items.every(it => isGeneric(it.item));
function extractKeywords(name: string) {
  const n = norm(name); const kws: string[] = [];
  if (/pilar|reforc|forma|viga|cinta/.test(n)) kws.push('formas','desmoldante','escoramento','espacadores','arame recozido','seguir projeto');
  if (/derivar|lavabo|pia/.test(n)) kws.push('tubos/conexões água (confirmar CPVC/PPR/PEX)','registro','fita veda rosca','esgoto DN 40/50');
  if (/dreno/.test(n)) kws.push('PVC esgoto 50/75','ralo sifonado','cola/primer','areia/cimento');
  if (/caixinh|pontos eletr|infra|eletrodut/.test(n)) kws.push('caixinhas 4x2/4x4','conduíte 20/25','caixa de passagem','conector','fita isolante','gesso/argamassa','tampa cega','bucha e parafuso','etiquetas de circuito');
  if (/pintur|parede provisoria/.test(n)) kws.push('selador','tinta','massa corrida','lixas','fita crepe','plásticos de proteção');
  if (/tapum|janela|esquadr/.test(n)) kws.push('painel de tapume','sarrafos','parafusos');
  return Array.from(new Set(kws)).slice(0,10);
}

async function gerarPorServico(rows: ServicoRow[]) {
  const sys = `Você é o Assistente ACOR.
Tarefa: gerar SUGESTÕES DE ITENS por SERVIÇO, sem quantidade e sem unidade, exclusivamente com base no texto do serviço (nome/descrição).
Regras:
1) Proíba respostas genéricas (ex.: “EPIs”, “proteções de área”) como única saída. Sempre liste itens concretos pertinentes ao serviço (mínimo 5 por serviço).
2) Estrutural: não prescrever concreto/aço sem projeto; foque em auxiliares (formas, desmoldante, escoramento, espaçadores, arame recozido) e mencione “seguir projeto” se aplicável.
3) Hidráulica: indicar “confirmar padrão (CPVC/PPR/PEX)” quando aplicável.
4) Não listar ferramentas/mão de obra; apenas materiais/insumos.
5) Evite repetir o mesmo pacote entre serviços; inclua ao menos 2 itens específicos por serviço (diferenciadores).
6) Formato OBRIGATÓRIO: JSON conforme schema.`;
  const schema = {
    name: 'materiais_por_servico',
    schema: {
      type: 'object',
      properties: {
        por_servico: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              servico_id: { type: 'string' },
              servico_nome: { type: 'string' },
              itens: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: { item: { type: 'string' }, motivo: { type: 'string' } },
                  required: ['item']
                },
                minItems: 5
              }
            },
            required: ['servico_id','servico_nome','itens']
          }
        },
        observacoes_gerais: { type: 'string' }
      },
      required: ['por_servico']
    }
  };
  // 1ª chamada
  let { parsed, text } = await openaiService.chat({
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: sys },
      { role: 'user', content: `SERVICOS_JSON:\n${JSON.stringify(rows.map(r=>({ id:r.id, nome:r.nome, descricao:r.descricao||null })), null, 2)}` }
    ],
    response_format: { type: 'json_schema', json_schema: schema },
    max_tokens: 5000,
    temperature: 0.2
  });
  let out: any = parsed ?? (()=>{ try { return JSON.parse(text); } catch { return null; }})();

  // Diversidade simples: detecta repetição forte entre serviços e pede ajuste (1 retry)
  const dupStats = () => {
    if (!out?.por_servico) return { bad:true, dupeSet:new Set<string>() };
    const f = new Map<string, number>(), nrm = (x:string)=>norm(x);
    for (const s of out.por_servico) {
      const seen = new Set<string>();
      for (const it of (s.itens||[])) {
        if (!it?.item) continue;
        const k = nrm(it.item); if (seen.has(k)) continue; seen.add(k);
        f.set(k, (f.get(k)||0)+1);
      }
    }
    const dupeSet = new Set([...f.entries()].filter(([,c])=>c>=3).map(([k])=>k));
    let heavy = 0;
    for (const s of out.por_servico) {
      const its = (s.itens||[]).filter((i:any)=>i?.item);
      if (!its.length) continue;
      const d = its.filter((i:any)=>dupeSet.has(nrm(i.item))).length;
      if (d/its.length >= 0.6) heavy++;
    }
    return { bad: heavy>=2, dupeSet };
  };
  const { bad, dupeSet } = dupStats();
  const needsRetry = !out?.por_servico?.length || out.por_servico.some((s:any)=> tooGenericService(s.itens)) || bad;

  if (needsRetry) {
    const hints = rows.reduce<Record<string,string[]>>((acc, r) => {
      acc[r.id] = extractKeywords(r.nome); return acc;
    }, {});
    const critique = `Sua resposta anterior repetiu itens demais entre serviços e/ou usou saídas genéricas.
Reescreva com diversidade:
- Itens repetidos em muitos serviços (evite repeti-los sem justificativa específica): ${[...dupeSet].slice(0,10).join(', ') || '(n/a)'}
- Para cada serviço, inclua pelo menos 2 itens específicos e pertinentes (diferenciadores).
Use estas palavras-chave extraídas do nome do serviço quando fizer sentido:
HINTS_JSON:\n${JSON.stringify(hints, null, 2)}
Exija no mínimo 5 itens por serviço, concretos e pertinentes. Proibido listar apenas EPIs/proteções.`;
    ({ parsed, text } = await openaiService.chat({
      model: 'gpt-4o',
      messages: [
        { role: 'system', content: sys },
        { role: 'user', content: `SERVICOS_JSON:\n${JSON.stringify(rows.map(r=>({ id:r.id, nome:r.nome, descricao:r.descricao||null })), null, 2)}` },
        { role: 'user', content: critique }
      ],
      response_format: { type: 'json_schema', json_schema: schema },
      max_tokens: 5200,
      temperature: 0.2
    }));
    out = parsed ?? (()=>{ try { return JSON.parse(text); } catch { return null; }})();
  }
  return out;
}

/* =========================
   Classe AssistenteService (mantendo seus fluxos)
   ========================= */

class AssistenteService {
  private contextCache: Map<string, { context: string; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  private lastObraId: string | null = null; // Memoriza o último obraId usado

  private async fileToDataURL(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    const mime = file.type || 'image/jpeg';
    return `data:${mime};base64,${b64}`;
  }

  /**
   * Processa mensagem com contexto dinâmico da obra atual
   */
  async processMessage(
    message: string,
    opts?: { obraId?: string }
  ): Promise<string> {
    const res = await resolveObraId({ explicitObraId: opts?.obraId, userMessage: message });
    const resolvedObraId = res.obraId;

    // Memoriza o obraId se encontrado
    if (resolvedObraId) {
      this.lastObraId = resolvedObraId;
    }

    try {

      console.log('🤖 Processando mensagem:', {
        message: message.substring(0, 50) + '...',
        requestedObraId: opts?.obraId,
        resolvedObraId: res.obraId,
        resolvedFrom: res.source,
        confidence: res.confidence
      });

      // 0) INTENÇÕES → RPCs (somente leitura)
      const msg = message || '';
      if (resolvedObraId) {
        if (PT.termometro.test(msg))        return await handleTermometro(resolvedObraId, res.obraNome);
        if (PT.prazos.test(msg))            return await handlePrazosSemana(resolvedObraId, res.obraNome);
        if (PT.subetapasAtraso.test(msg))   return await handleSubetapasAtraso(resolvedObraId, res.obraNome);
        if (PT.servicosRisco.test(msg))     return await handleServicosEmRisco(resolvedObraId, res.obraNome);
        if (PT.comprasRisco.test(msg))      return await handleComprasRisco(resolvedObraId, res.obraNome, 7);
        if (PT.threads.test(msg))           return await handleThreads(resolvedObraId, res.obraNome);
        if (PT.pendencias.test(msg))        return await handlePendencias(resolvedObraId, res.obraNome);
        if (PT.ocorrencias.test(msg))       return await handleOcorrencias(resolvedObraId, res.obraNome, 14);
        if (PT.clima.test(msg))             return await handleClima(resolvedObraId, res.obraNome, 30);
        if (PT.efetivo.test(msg))           return await handleEfetivo(resolvedObraId, res.obraNome, 14);
        if (PT.checklists.test(msg))        return await handleChecklists(resolvedObraId, res.obraNome, 30);
        if (PT.parados.test(msg))           return await handleParados(resolvedObraId, res.obraNome);
        if (/(progresso|percentual|percent|%)[\s\S]*(empresa|empreiteira|fornecedor)/.test(msg)) {
          return await handleProgressoPorEmpresa(resolvedObraId, res.obraNome);
        }
      }

      // 1) Caso especial já existente: ALTERAÇÕES → ler coluna `alteracoes_projeto`
      if (res.obraId && isPerguntaAlteracoes(message)) {
        const regs = await fetchAlteracoesProjetoDaObra(res.obraId);
        const rotuloObra = res.obraNome ? res.obraNome : `#${res.obraId.slice(0, 8)}…`;
        if (!regs.length) {
          return `📍 **Obra**: ${rotuloObra}\nNão há registros na coluna **alterações de projeto** dos Diários desta obra.`;
        }
        const linhas: string[] = [];
        linhas.push(`📍 **Obra**: ${rotuloObra}`);
        linhas.push(`**Alterações de Projeto — Diário de Obras**`);
        for (const r of regs) {
          const num = r.numero != null ? `#${r.numero}` : '';
          if (!r.linhas.length) continue;
          // Uma linha por item da coluna JSONB (sem alterar o conteúdo semântico)
          for (const l of r.linhas) {
            linhas.push(`- Relatório ${num}: ${l} _(id:${r.relatorio_id})_`);
          }
        }
        return linhas.join('\n');
      }

      /* ===== 0) INTELIGÊNCIA DE SERVIÇOS ⇒ MATERIAIS ===== */
      const catIntent = detectSugestoesPorCategoria(message);
      if (catIntent) {
        const obraIdCat =
          opts?.obraId ??
          (await (contextService as any)?.getObraIdAtual?.()) ??
          (await (contextService as any)?.getObraAtualId?.()) ??
          (await (contextService as any)?.getObraContext?.())?.obraId ??
          this.lastObraId ?? null;
        if (!obraIdCat) return 'Selecione uma obra para listar os serviços da categoria.';
        const { ini, fim, rows } = await fetchServicosSemana(obraIdCat, catIntent.scope);
        const filtrados = rows.filter(r => matchCategoria(r.nome, catIntent.cat));
        if (!filtrados.length) return `🧰 **Materiais sugeridos — ${catIntent.cat}** (${ini} a ${fim})\n\nNão encontrei serviços dessa categoria na janela.`;
        const payload = await gerarPorServico(filtrados);
        const canon   = normalizeModelOutput(payload, filtrados);
        // Lista Única da Categoria
        const lista = consolidarListaUnicaDeEntradas((canon?.por_servico)||[], filtrados);
        const titulo = `🧰 **Materiais sugeridos — ${catIntent.cat.charAt(0).toUpperCase()+catIntent.cat.slice(1)}** (${ini} a ${fim})`;
        const linhas: string[] = [titulo, `\n### ${catIntent.cat.charAt(0).toUpperCase()+catIntent.cat.slice(1)}`];
        if (!lista.length) linhas.push('• (sem itens)');
        for (const it of lista) {
          const freq = it.qtdeServicos>1 ? ` (${it.qtdeServicos} serviços)` : '';
          const ex   = it.exemplos.length ? ` — _ex.: ${it.exemplos.join('; ')}_` : '';
          linhas.push(`• ${it.label}${freq}${ex}`);
        }
        return linhas.join('\n');
      }

      const semIntent = detectSugestoesSemana(message);
      if (semIntent) {
        const obraIdSem =
          opts?.obraId ??
          (await (contextService as any)?.getObraIdAtual?.()) ??
          (await (contextService as any)?.getObraAtualId?.()) ??
          (await (contextService as any)?.getObraContext?.())?.obraId ??
          this.lastObraId ?? null;
        if (!obraIdSem) return 'Selecione uma obra para gerar as sugestões da semana.';
        const { ini, fim, rows } = await fetchServicosSemana(obraIdSem, semIntent.scope);
        if (!rows.length) return `🧰 **Sugestões de Materiais da Semana** (${ini} a ${fim})\n\nNenhum serviço agendado/em andamento na janela.`;
        const payload = await gerarPorServico(rows);
        const canon   = normalizeModelOutput(payload, rows);
        // Consolida em lista única por categoria
        const blocos  = consolidarPorCategoria(canon!, rows);
        const linhas: string[] = [`🧰 **Sugestões de Materiais da Semana** (${ini} a ${fim})`];
        if (!blocos.length) linhas.push('\nNão encontrei itens para consolidar.');
        for (const bloco of blocos) {
          linhas.push(`\n### ${bloco.categoria}`);
          for (const it of bloco.itens) {
            const freq = it.qtdeServicos>1 ? ` (${it.qtdeServicos} serviços)` : '';
            const ex   = it.exemplos.length ? ` — _ex.: ${it.exemplos.join('; ')}_` : '';
            linhas.push(`• ${it.label}${freq}${ex}`);
          }
        }
        return linhas.join('\n');
      }

      /* ===== 1) DIÁRIOS (mantido conforme seu serviço atual) ===== */
      const dIntent = detectDiariosIntent(message);
      if (dIntent) {
        // 1) Resolver obraId como nos outros módulos
        const obraId =
          opts?.obraId ??
          (await (contextService as any)?.getObraIdAtual?.()) ??
          (await (contextService as any)?.getObraAtualId?.()) ??
          (await (contextService as any)?.getObraContext?.())?.obraId ??
          null;
        if (!obraId) return 'Selecione uma obra para consultar os diários.';

        // 2) Helper de busca direta (aceita tipo NULL ou 'relatorio_diario')
        const fetchDiarios = async (de?: string, ate?: string, limit?: number) => {
          let q = supabase
            .from('relatorios')
            .select('id, obra_id, numero, data, status, tipo, clima_manha, clima_tarde, clima_noite, mao_obra, equipamentos, atividades, ocorrencias, comentarios, alteracoes_projeto')
            .eq('obra_id', obraId)
            .or('tipo.is.null,tipo.eq.relatorio_diario')
            .order('data', { ascending: false })
            .order('numero', { ascending: false });
          if (de && ate) q = q.gte('data', de).lte('data', ate);
          if (limit) q = q.limit(limit);
          const { data, error } = await q;
          if (error) throw error;
          return (data ?? []) as any[];
        };

        // Janela base
        const hoje = new Date();
        const de7 = ymdLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 6));
        const de30 = ymdLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 29));
        const ate = ymdLocal(hoje);

        // 3) Escolher a janela (semana → 7 dias; padrão → 30 dias)
        const base = (dIntent.scope === 'semana')
          ? await fetchDiarios(de7, ate)
          : await fetchDiarios(de30, ate);

        // Fallbacks: se vazio, tenta 30 dias; se ainda vazio, top 10 sem data
        const lista = base.length ? base
                    : (await fetchDiarios(de30, ate)).length ? await fetchDiarios(de30, ate)
                    : await fetchDiarios(undefined, undefined, 10);

        if (!lista.length) {
          return dIntent.scope === 'semana'
            ? `📓 **Diários da semana**\nPeríodo: ${de7} → ${ate}\nNenhum diário no período.`
            : `📓 **Diários (últimos 30 dias)**\nPeríodo: ${de30} → ${ate}\nNenhum diário encontrado.`;
        }

        // 4) Renderização por foco
        const bullets = (arr: string[]) => arr.filter(Boolean).join('\n');

        if (dIntent.focus === 'comentarios') {
          const linhas = lista
            .filter(r => r.comentarios && String(r.comentarios).trim().length)
            .slice(0, 15)
            .map(r => `- ${r.data} #${r.numero} — ${String(r.comentarios).trim().slice(0, 240)}${String(r.comentarios).length > 240 ? '…' : ''}`);
          return linhas.length
            ? `💬 **Comentários dos diários**\n${bullets(linhas)}`
            : '💬 **Comentários dos diários**\nNenhum comentário registrado no período.';
        }

        if (dIntent.focus === 'alteracoes') {
          // alteracoes_projeto: pode ser array, objeto ou texto
          const linhas: string[] = [];
          for (const r of lista) {
            const ap = r.alteracoes_projeto;
            if (!ap) continue;
            let texto = '';
            try {
              if (Array.isArray(ap)) {
                const itens = ap.map((it: any) =>
                  typeof it === 'string' ? it :
                  it?.descricao ?? it?.descricao_resumida ?? it?.titulo ?? JSON.stringify(it)
                ).filter(Boolean);
                if (itens.length) texto = itens.join('; ');
              } else if (typeof ap === 'object') {
                // tenta extrair campos comuns
                const possiveis = ['descricao', 'descricao_resumida', 'titulo', 'observacoes', 'motivo'];
                const achados = possiveis.map(k => ap?.[k]).filter(Boolean);
                texto = achados.length ? achados.join('; ') : JSON.stringify(ap);
              } else {
                texto = String(ap);
              }
            } catch {
              texto = String(ap);
            }
            if (texto && texto.trim().length) {
              linhas.push(`- ${r.data} #${r.numero} — ${texto.slice(0, 240)}${texto.length > 240 ? '…' : ''}`);
            }
            if (linhas.length >= 15) break;
          }
          return linhas.length
            ? `🛠️ **Alterações de projeto (diários)**\n${bullets(linhas)}`
            : '🛠️ **Alterações de projeto (diários)**\nNenhum registro de alteração no período.';
        }

        if (dIntent.focus === 'atividades') {
          const linhas = lista
            .filter(r => r.atividades && String(r.atividades).trim().length)
            .slice(0, 15)
            .map(r => `- ${r.data} #${r.numero} — ${String(r.atividades).trim().slice(0, 240)}${String(r.atividades).length > 240 ? '…' : ''}`);
          return linhas.length
            ? `🧱 **Atividades registradas**\n${bullets(linhas)}`
            : '🧱 **Atividades registradas**\nNenhuma atividade descrita no período.';
        }

        if (dIntent.focus === 'ocorrencias') {
          const linhas = lista
            .filter(r => r.ocorrencias && String(r.ocorrencias).trim().length)
            .slice(0, 15)
            .map(r => `- ${r.data} #${r.numero} — ${String(r.ocorrencias).trim().slice(0, 240)}${String(r.ocorrencias).length > 240 ? '…' : ''}`);
          return linhas.length
            ? `⚠️ **Ocorrências**\n${bullets(linhas)}`
            : '⚠️ **Ocorrências**\nNenhuma ocorrência registrada no período.';
        }

        if (dIntent.focus === 'clima') {
          const linhas = lista.slice(0, 15).map(r => {
            const partes = [r.clima_manha, r.clima_tarde, r.clima_noite].filter(Boolean).join(' / ');
            return `- ${r.data} #${r.numero} — ${partes || '-'}`;
          });
          return `🌦 **Clima nos diários**\n${bullets(linhas)}`;
        }

        if (dIntent.focus === 'mao_obra') {
          const linhas: string[] = [];
          for (const r of lista.slice(0, 15)) {
            const mo = r.mao_obra;
            if (!mo) continue;
            let resumo = '';
            try {
              if (Array.isArray(mo)) {
                // tenta extrair função/quantidade
                const itens = mo.map((it: any) => {
                  const f = it?.funcao ?? it?.cargo ?? it?.equipe ?? 'mão de obra';
                  const q = it?.quantidade ?? it?.qtd ?? it?.pessoas ?? '';
                  return q ? `${f}: ${q}` : String(f);
                });
                resumo = itens.join('; ');
              } else if (typeof mo === 'object') {
                // objeto com chaves -> quantidades
                const itens = Object.entries(mo).map(([k, v]) => `${k}: ${v as any}`);
                resumo = itens.join('; ');
              } else {
                resumo = String(mo);
              }
            } catch {
              resumo = JSON.stringify(mo);
            }
            linhas.push(`- ${r.data} #${r.numero} — ${resumo.slice(0, 240)}${resumo.length > 240 ? '…' : ''}`);
          }
          return linhas.length
            ? `👷 **Mão de obra**\n${bullets(linhas)}`
            : '👷 **Mão de obra**\nSem registros de mão de obra no período.';
        }

        if (dIntent.focus === 'equipamentos') {
          const linhas: string[] = [];
          for (const r of lista.slice(0, 15)) {
            const eq = r.equipamentos;
            if (!eq) continue;
            let resumo = '';
            try {
              if (Array.isArray(eq)) {
                const itens = eq.map((it: any) => it?.nome ?? it?.equipamento ?? JSON.stringify(it));
                resumo = itens.join('; ');
              } else if (typeof eq === 'object') {
                const itens = Object.entries(eq).map(([k, v]) => `${k}: ${v as any}`);
                resumo = itens.join('; ');
              } else {
                resumo = String(eq);
              }
            } catch {
              resumo = JSON.stringify(eq);
            }
            linhas.push(`- ${r.data} #${r.numero} — ${resumo.slice(0, 240)}${resumo.length > 240 ? '…' : ''}`);
          }
          return linhas.length
            ? `🏗️ **Equipamentos**\n${bullets(linhas)}`
            : '🏗️ **Equipamentos**\nSem registros de equipamentos no período.';
        }

        // RESUMO (padrão)
        const total = lista.length;
        const aprov = lista.filter(r => r.status === 'aprovado').length;
        const subm  = lista.filter(r => r.status === 'submetido').length;
        const rasc  = lista.filter(r => r.status === 'rascunho').length;

        const hasAtiv = lista.some(r => r.atividades && String(r.atividades).trim().length);
        const hasOcor = lista.some(r => r.ocorrencias && String(r.ocorrencias).trim().length);
        const hasComent = lista.some(r => r.comentarios && String(r.comentarios).trim().length);

        const head = dIntent.scope === 'semana'
          ? `📓 **Diários da semana**\nPeríodo: ${de7} → ${ate}`
          : `📓 **Diários (últimos 30 dias)**\nPeríodo: ${de30} → ${ate}`;

        const linhasResumo = [
          `${head}`,
          `• Total: ${total}`,
          `• Aprovados: ${aprov} | Submetidos: ${subm} | Rascunhos: ${rasc}`,
          hasAtiv ? '• Há atividades registradas.' : '• Sem atividades registradas.',
          hasOcor ? '• Há ocorrências registradas.' : '• Sem ocorrências registradas.',
          hasComent ? '• Há comentários registrados.' : '• Sem comentários registrados.',
          '',
          'Mais recentes:',
          ...lista.slice(0, 5).map(it =>
            `- ${it.data} #${it.numero} — ${it.status ?? '-'}${it.atividades ? ` | Atividades: ${String(it.atividades).slice(0, 80)}${String(it.atividades).length > 80 ? '…' : ''}` : ''}`
          )
        ];
        return linhasResumo.join('\n');
      }

      /* ===== 2) Fluxo padrão com contexto da obra ===== */

      // Buscar contexto da obra se disponível
      let obraContext = '';
      if (resolvedObraId) {
        obraContext = await this.getObraContext(resolvedObraId);
      }

      // Processar com OpenAI
      const response = await openaiService.processTextMessage(
        message,
        obraContext
      );

      console.log('✅ Resposta gerada com sucesso');
      return response;

    } catch (error) {
      console.error('❌ Erro ao processar mensagem:', error);
      return this.getFallbackResponse(message, resolvedObraId);
    }
  }

  /**
   * Analisa imagem enviada pelo usuário
   */
  async analyzeImage(
    image: File,
    userQuestion?: string,
    obraId?: string
  ): Promise<string> {
    const res = await resolveObraId({ explicitObraId: obraId, userMessage: userQuestion });
    
    try {
      console.log('📸 Analisando imagem:', {
        fileName: image.name,
        size: image.size,
        type: image.type,
        requestedObraId: obraId,
        resolvedObraId: res.obraId,
        resolvedFrom: res.source,
        confidence: res.confidence
      });

      // Verificar se é uma imagem válida
      if (!image.type.startsWith('image/')) {
        return '❌ Por favor, envie apenas arquivos de imagem (JPG, PNG, etc.)';
      }

      // Verificar tamanho (limite de 10MB)
      if (image.size > 10 * 1024 * 1024) {
        return '❌ Imagem muito grande. Por favor, envie arquivos menores que 10MB.';
      }

      // Buscar contexto da obra se disponível
      let obraContext = '';
      if (res.obraId) {
        obraContext = await this.getObraContext(res.obraId);
      }

      // Análise multimodal real (imagem + texto)
      const dataUrl = await this.fileToDataURL(image);
      const response = await openaiService.processMultimodalMessage(
        userQuestion || 'Analise tecnicamente esta imagem da obra',
        dataUrl,
        obraContext
      );

      console.log('✅ Análise de imagem concluída');
      return response;

    } catch (error) {
      console.error('❌ Erro ao analisar imagem:', error);
      return this.getImageAnalysisFallback();
    }
  }

  /**
   * Busca e cacheia contexto da obra
   */
  private async getObraContext(obraId: string): Promise<string> {
    try {
      // Verificar cache
      const cached = this.contextCache.get(obraId);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        console.log('📦 Usando contexto em cache para obra:', obraId);
        return cached.context;
      }

      // Buscar contexto completo
      const obraCompleta = await contextService.getObraCompleta(obraId);

      if (!obraCompleta) {
        console.warn('⚠️ Obra não encontrada:', obraId);
        return '';
      }

      // Gerar texto formatado
      const contextText = await contextService.generateContextText(obraCompleta);

      // Cachear resultado
      this.contextCache.set(obraId, {
        context: contextText,
        timestamp: Date.now()
      });

      console.log('✅ Contexto da obra carregado e cacheado');
      return contextText;

    } catch (error) {
      console.error('❌ Erro ao buscar contexto da obra:', error);
      return '';
    }
  }

  /**
   * Limpa cache de contexto (usar quando obra for atualizada)
   */
  clearContextCache(obraId?: string): void {
    if (obraId) {
      this.contextCache.delete(obraId);
      console.log('🧹 Cache limpo para obra:', obraId);
    } else {
      this.contextCache.clear();
      console.log('🧹 Todo cache de contexto limpo');
    }
  }

  /**
   * Resposta de fallback quando há erro
   */
  private getFallbackResponse(message: string, obraId?: string): string {
    const msgLower = message.toLowerCase();

    // Respostas inteligentes baseadas em palavras-chave
    if (msgLower.includes('cronograma') || msgLower.includes('prazo')) {
      return `🗓️ **Cronograma da Obra - ACOR**

${obraId ? 'Para consultar o cronograma atual:\n' : ''}• Acesse a seção "Cronograma" no menu da obra
• Verifique prazos e dependências
• Acompanhe o progresso em tempo real

**Valores ACOR em ação:**
🏃‍♂️ **Agilidade**: Monitoramento constante de prazos
📃 **Organização**: Cronograma estruturado e atualizado

*A IA está temporariamente indisponível, mas você pode acessar todas as informações diretamente no sistema!*`;
    }

    if (msgLower.includes('orçamento') || msgLower.includes('valor') || msgLower.includes('custo')) {
      return `💰 **Orçamento da Obra - ACOR**

Para informações detalhadas de orçamento:
• Consulte a aba "Orçamento" da obra
• Verifique relatórios de custos
• Acompanhe gastos vs. planejado

**Transparência ACOR:**
🤝 **Confiança**: Informações claras e atualizadas
🎯 **Resultado**: Controle rigoroso de custos

*Consulte o responsável da obra para análises específicas.*`;
    }

    if (msgLower.includes('tarefa') || msgLower.includes('responsável')) {
      return `✅ **Gestão de Tarefas - ACOR**

Para consultar e gerenciar tarefas:
• Acesse "Tarefas" no menu da obra
• Verifique responsáveis e status
• Atualize progresso em tempo real

**Eficiência ACOR:**
📃 **Organização**: Tarefas bem estruturadas
🎯 **Resultado**: Foco na entrega de qualidade

*Use o sistema para atualizar status e adicionar comentários!*`;
    }

    // Resposta padrão
    return `🧠 **Assistente ACOR - Modo Offline**

Estou temporariamente indisponível, mas você pode:

• **Consultar informações** diretamente no sistema
• **Acessar documentos** da obra atual
• **Verificar cronograma** e prazos
• **Gerenciar tarefas** e responsabilidades

**Os valores ACOR continuam guiando nosso trabalho:**
🏃‍♂️ Agilidade | 🤝 Confiança | 📃 Organização | 🎯 Resultado

*Entre em contato com o suporte se precisar de ajuda específica!*`;
  }

  /**
   * Fallback para análise de imagens
   */
  private getImageAnalysisFallback(): string {
    return `📸 **Análise de Imagem - ACOR (Modo Manual)**

A análise automática está indisponível. Siga estas diretrizes ACOR:

**✅ VERIFICAÇÃO MANUAL:**
• Confirme conformidade com projetos
• Verifique padrões de qualidade ACOR
• Valide aspectos de segurança
• Compare com especificações técnicas

**📃 DOCUMENTAÇÃO:**
• Registre no diário da obra
• Anexe fotos aos relatórios
• Comunique não-conformidades

**🛠️ EM CASO DE DÚVIDAS:**
• Consulte o responsável técnico
• Revisite documentos do projeto
• Siga procedimentos ACOR estabelecidos

*A qualidade e segurança são sempre prioridades ACOR!*`;
  }
}

export const assistenteService = new AssistenteService();
